import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from 'components/ui/button'
import { usePartner, usePartnerReferrals } from 'hooks/usePartnerAdmin'
import PartnersLayout from './PartnersLayout'
import PartnerProfileForm from './PartnerProfileForm'
import LedgerTable from './LedgerTable'
import ReferralsTable from './ReferralsTable'
import AdjustmentModal from './AdjustmentModal'
import { formatDate, formatUSDC } from './format'

const Stat: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <div className="border border-neutral-200 rounded-lg p-3">
    <div className="text-xs uppercase text-neutral-500">{label}</div>
    <div className="text-lg font-semibold">{value}</div>
  </div>
)

const PartnerDetail: React.FC = () => {
  const { partnerId } = useParams<{ partnerId: string }>()
  const id = partnerId && /^\d+$/.test(partnerId) ? Number(partnerId) : null
  const { data, error, isLoading, mutate } = usePartner(id)
  const referrals = usePartnerReferrals(id)
  const [adjustOpen, setAdjustOpen] = useState(false)

  if (id === null) {
    return <PartnersLayout title="Partner">Invalid partner id.</PartnersLayout>
  }
  if (isLoading) {
    return <PartnersLayout title="Partner">Loading…</PartnersLayout>
  }
  if (error || !data) {
    return (
      <PartnersLayout title="Partner">
        <div className="text-sm text-red-600">
          Failed to load partner: {(error as Error)?.message ?? 'not found'}
        </div>
        <Link to="/partners" className="text-brand-primary text-sm">
          Back to partners
        </Link>
      </PartnersLayout>
    )
  }

  const { partner, stats } = data
  const campaign_links = data.campaign_links ?? []
  const ledger = data.ledger ?? []

  return (
    <PartnersLayout
      title={`Partner · ${partner.handle}`}
      actions={
        <Button variant="outline" onClick={() => setAdjustOpen(true)}>
          Add adjustment
        </Button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Stat label="Sign-ups" value={stats.sign_ups} />
        <Stat label="Active referrals" value={stats.active_referrals} />
        <Stat label="Earned" value={formatUSDC(stats.earned_all_time_usdc)} />
        <Stat label="Claimable" value={formatUSDC(stats.claimable_usdc)} />
        <Stat label="Pending" value={formatUSDC(stats.pending_usdc)} />
        <Stat label="Paid" value={formatUSDC(stats.paid_usdc)} />
      </div>

      <div className="space-y-6">
        <PartnerProfileForm partner={partner} onSaved={() => mutate()} />

        <section>
          <h2 className="font-semibold mb-2">Campaign links</h2>
          {campaign_links.length === 0 ? (
            <div className="text-sm text-neutral-600">No campaign links.</div>
          ) : (
            <ul className="text-sm space-y-1">
              {campaign_links.map((l) => (
                <li key={l.id}>
                  <span className="font-mono">{l.code}</span> · {l.label} ·{' '}
                  {formatDate(l.created_at)}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="font-semibold mb-2">Referrals</h2>
          {referrals.isLoading ? (
            <div className="text-sm text-neutral-600">Loading…</div>
          ) : referrals.error ? (
            <div className="text-sm text-red-600">
              Failed to load referrals:{' '}
              {(referrals.error as Error)?.message ?? 'unknown error'}
            </div>
          ) : (
            <ReferralsTable referrals={referrals.data ?? []} />
          )}
        </section>

        <section>
          <h2 className="font-semibold mb-2">Ledger (latest 200)</h2>
          <LedgerTable entries={ledger} />
        </section>
      </div>

      <AdjustmentModal
        open={adjustOpen}
        partnerId={partner.id}
        onClose={() => setAdjustOpen(false)}
        onCreated={() => {
          setAdjustOpen(false)
          mutate()
        }}
      />
    </PartnersLayout>
  )
}

export default PartnerDetail
